import React, { useCallback, useState } from 'react';
import * as navigate from 'app/area/shared/navigation';
import { MainGridContainer } from 'ui/common/main-grid-container/main-grid-container';
import { ComplexValue, LoadOptions, RequestRemoteApi } from 'app/types-shared';
import { ApplicationState } from 'app/types-store';
import { OverlayComponent } from 'app/components';
import { SwitchDisplayDisabled } from 'app/area/shared/saved-reports/components/switch-display-disabled';
import { SavedReportValidation } from 'app/area/shared/saved-reports/types-store';
import { LoadEvaluationHandler, OnUpdateSavedReport, UpdateSavedReportHandler } from 'app/area/shared/saved-reports/types';
import { createDefaultSavedReport } from '../../common/create-default-saved-report';
import { getBackRoute } from '../../common/route';
import { SavedReportDashboard } from '../../common/types-store';
import { DashboardAdminTranslation } from '../../constants';
import { endpoints } from '../endpoints';
import { DashboardSavedReportsState } from '../types-store';
import { SavedReportsHeader } from './saved-reports-header';
import { SavedReportsList } from './saved-reports-list';
import { SavedReportsModal } from './saved-reports-modal';
import { validateSavedReport } from '../../common/validate-saved-report';
import { DashboardItemValidation } from 'app/area/dashboard-administration/form/types-store';
import { processSavedReports } from 'app/area/shared/saved-reports/utils/process-saved-reports';
import { PopupAddReports } from 'app/area/dashboard-administration/saved-reports/components/popup-add-reports';
import { mySavedReportsId } from 'app/area/dashboard-administration/saved-reports/constants';
import { SavedReportCategory } from 'app/area/shared/saved-reports/constants';
import styles from './saved-reports-renderer.scss';

interface SavedReportsRendererProps {
  savedReportsState: DashboardSavedReportsState;
  dashboardTypeId: number;
  searchFavouriteId: number;
  options: {
    [key: string]: {
      fetching: boolean;
      codes?: ComplexValue<number | string>[];
    };
  };
  locale: string;
  translation: DashboardAdminTranslation;
  validation: DashboardItemValidation;
  saveSavedReports: () => void;
  selectCurrentSavedReports: (savedReport: SavedReportDashboard, onlyIdMode: boolean) => void;
  updateCurrentSavedReports: (savedReport: Partial<SavedReportDashboard>) => void;
  updateCurrentValidation: (validation: SavedReportValidation<SavedReportDashboard>) => void;
  loadDashboardEvaluation: LoadEvaluationHandler<SavedReportDashboard>;
  updateSavedReport: UpdateSavedReportHandler<SavedReportDashboard>;
  deleteSavedReport: (savedReportId: number) => void;
  loadOptions: (id: string,
    componentEndpoint: string,
    loadOptionsParams: LoadOptions,
    optionsParams?: any) => void;
  displayError: (errorMessage: string) => void;
  switchDisplayDisabled: (displayDisabled: boolean) => void;
}

// tslint:disable-next-line: max-func-body-length
export const SavedReportsRenderer: React.FC<SavedReportsRendererProps> = ({
  dashboardTypeId, searchFavouriteId, savedReportsState, options, locale, translation,
  selectCurrentSavedReports, updateCurrentSavedReports, updateCurrentValidation,
  loadDashboardEvaluation, updateSavedReport, deleteSavedReport, saveSavedReports,
  switchDisplayDisabled, loadOptions, displayError
}) => {

  const {
    description, saving, displayDisabled, currentSavedReportId, currentSavedReport,
    currentSavedReportOnlyIdMode, currentValidation, savedReports, savedReportsValidation
  } = savedReportsState;

  const [showReportIdModal, switchShowReportIdModal] = useState(false);

  const allSavedReportIs = savedReports?.map(report => report.code?.value);

  const addSavedReports = (currentSavedReportDashboard: SavedReportDashboard) =>
    processSavedReports({
      currentSavedReport: currentSavedReportDashboard,
      currentSavedReportId,
      allSavedReportIs,
      updateSavedReport,
      validateSavedReport,
      updateValidation: updateCurrentValidation,
      loadEvaluation, displayError, onClose: closeReportsModal, validation: currentValidation, translation
    });

  const [showReportModal, switchShowReportModal] = useState(false);

  const loadSavedReportOptions = (loadOptionsParams: LoadOptions) => {
    loadOptions(mySavedReportsId, endpoints.savedReportsOptions, {
      ...loadOptionsParams,
      requestRemoteApi: RequestRemoteApi.servicesApi
    },
    {
      reportCategory: SavedReportCategory.statisticalReport
    });
  };

  const addReports = useCallback(() => {
    const savedReportDoge = createDefaultSavedReport(undefined, savedReports.length + 1);

    selectCurrentSavedReports(savedReportDoge, false);
    switchShowReportModal(true);
  }, [savedReports.length]);

  const closeAddSavedReportsHandler = () => {
    selectCurrentSavedReports(undefined, false);
    switchShowReportModal(false);
  };

  const onAddSavedReports = useCallback((
    values: ComplexValue<number>[],
    savedReportDashboard: SavedReportDashboard
  ) => {
    const newSavedReports = values
      .filter(value => !allSavedReportIs.includes(value.value))
      .map((value, index) => createDefaultSavedReport(value, savedReportDashboard.sequence + index));

    newSavedReports.forEach(report => {
      selectCurrentSavedReports(report, false);
      addSavedReports(report)();
    });
  }, [savedReports.length]);

  const onManageSavedReports = () => {
    navigate.toSearchView(true);
  };

  const addReportById = useCallback(() => {
    selectCurrentSavedReports(createDefaultSavedReport(undefined, savedReports.length + 1), false);
    switchShowReportIdModal(true);
  }, [savedReports.length]);

  const editSavedReportById = useCallback((savedReport: SavedReportDashboard) => {
    selectCurrentSavedReports(savedReport, false);
    switchShowReportIdModal(true);
  }, [savedReports]);

  const editSavedReports = useCallback((savedReport: SavedReportDashboard) => {
    selectCurrentSavedReports(savedReport, false);
    switchShowReportModal(true);
  }, [savedReports]);

  const closeReportByIdModal = useCallback(() => {
    selectCurrentSavedReports(undefined, false);
    switchShowReportIdModal(false);
  }, []);

  const closeReportsModal = useCallback(() => {
    selectCurrentSavedReports(undefined, false);
    switchShowReportModal(false);
  }, []);

  const onCancel = useCallback(() => {
    navigate.to({ pathname: getBackRoute(dashboardTypeId, searchFavouriteId) });
  }, []);

  const loadEvaluation = useCallback((favouriteId: number, onUpdateSavedReport: OnUpdateSavedReport<SavedReportDashboard>) => {
    const getState = (state: ApplicationState) => state?.dashboardAdministration?.savedReports;
    const onSavedReportError = () => {
      displayError(translation.fields_are_filled_out_incorrectly);
    };
    loadDashboardEvaluation(dashboardTypeId, favouriteId, onUpdateSavedReport, onSavedReportError, getState);
  }, [dashboardTypeId]);

  return <>
    <OverlayComponent
      displayOverlay={saving}
      overlayText={translation.saving_dashboard} />

    {showReportIdModal && !!currentSavedReport
      && <SavedReportsModal
        currentSavedReportId={currentSavedReportId}
        currentSavedReport={currentSavedReport}
        currentSavedReportOnlyIdMode={currentSavedReportOnlyIdMode}
        allSavedReportIs={allSavedReportIs}
        updateCurrent={updateCurrentSavedReports}
        updateSavedReport={updateSavedReport}
        closeModal={closeReportByIdModal}
        loadEvaluation={loadEvaluation}
        options={options}
        loadOptions={loadOptions}
        updateValidation={updateCurrentValidation}
        displayError={displayError}
        locale={locale}
        validation={currentValidation}
        translation={translation} />}

    {showReportModal
      && <PopupAddReports
        title={translation.add_saved_reports_title}
        currentSavedReport={currentSavedReport}
        addItems={onAddSavedReports}
        onClose={closeAddSavedReportsHandler}
        actionTitle={translation.add_new_report}
        onAction={onManageSavedReports}
        options={options?.[mySavedReportsId]?.codes as ComplexValue<number>[]}
        fetchingOptions={options?.[mySavedReportsId]?.fetching || false}
        loadOptions={loadSavedReportOptions}
        descriptionAllLevels={false}
        doneLabel={translation.done}
        searchPlaceholder={translation.search}
        locale={locale}
        validation={currentValidation}
      />}

    <SavedReportsHeader
      description={description}
      translation={translation}
      onSave={saveSavedReports}
      onCancel={onCancel}
      onAddReport={addReports}
      onAddReportById={addReportById}
    />
    <MainGridContainer className={`dashboard ${styles.dashboardView}`} wide={true}>
      <SwitchDisplayDisabled
        label={translation.show_disabled_elements}
        active={displayDisabled}
        onChange={switchDisplayDisabled} />
      <SavedReportsList
        savedReportsState={savedReportsState}
        editSavedReportById={editSavedReportById}
        editSavedReports={editSavedReports}
        updateSavedReport={updateSavedReport}
        deleteSavedReport={deleteSavedReport}
        onAddReport={addReports}
        onAddReportById={addReportById}
        validation={savedReportsValidation}
        translation={translation} />
    </MainGridContainer>
  </>;
};
